import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { apiFetch } from '@/lib/api'
import { notifyError } from '@/lib/query'
import type { ResourceField } from '@/lib/resources'
import { Eye } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

interface Props {
  field: ResourceField
  value: unknown
  resourceKey: string
  rowId?: string | number
  onRevealed?: (value: unknown) => void
}

/** Render a value for read display — formats by widget type. */
export function ResourceFieldRenderer({
  field,
  value,
  resourceKey,
  rowId,
  onRevealed
}: Props) {
  const [revealedValue, setRevealedValue] = useState<unknown>(undefined)
  const [revealing, setRevealing] = useState(false)

  const display = revealedValue !== undefined ? revealedValue : value

  if (field.redact && revealedValue === undefined && rowId !== undefined) {
    return (
      <div className="inline-flex items-center gap-2">
        <span className="text-muted-foreground tracking-widest">••••••••</span>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={revealing}
          onClick={async (e) => {
            e.stopPropagation()
            setRevealing(true)
            try {
              const result = await apiFetch<Record<string, unknown>>(
                `/api/admin/r/${encodeURIComponent(resourceKey)}/${encodeURIComponent(String(rowId))}/reveal/${encodeURIComponent(field.name)}/`,
                { method: 'POST' }
              )
              const v = result[field.name]
              setRevealedValue(v)
              onRevealed?.(v)
              toast('Revealed', {
                description: `${field.label} reveal logged to audit.`
              })
            } catch (err) {
              notifyError('Could not reveal', err)
            } finally {
              setRevealing(false)
            }
          }}
        >
          <Eye className="size-3" /> Reveal
        </Button>
      </div>
    )
  }

  if (display === null || display === undefined || display === '') {
    return <span className="text-muted-foreground">—</span>
  }

  if (field.choices && field.choices.length > 0) {
    const match = field.choices.find((c) => c.value === display)
    return <Badge variant="secondary">{match?.label ?? String(display)}</Badge>
  }

  switch (field.widget) {
    case 'switch':
      return display ? (
        <Badge variant="default">Yes</Badge>
      ) : (
        <Badge variant="outline">No</Badge>
      )
    case 'datetime':
    case 'date':
      return <span className="tabular-nums">{formatDate(display)}</span>
    case 'json':
      return (
        <code className="block max-h-40 overflow-auto rounded bg-muted px-2 py-1 text-xs">
          {JSON.stringify(display, null, 2)}
        </code>
      )
    case 'textarea':
      return (
        <span className="line-clamp-3 whitespace-pre-wrap">
          {String(display)}
        </span>
      )
    case 'fk':
      return <span className="font-mono text-xs">{String(display)}</span>
    default:
      return <span>{String(display)}</span>
  }

  function formatDate(v: unknown): string {
    if (typeof v !== 'string' && !(v instanceof Date)) return String(v)
    const d = typeof v === 'string' ? new Date(v) : v
    if (Number.isNaN(d.getTime())) return String(v)
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }
}
